import { useState, useCallback, useMemo } from "react";
import {
  ContextualSaveBar,
  Page,
  Layout,
  Card,
  BlockStack,
  Loading,
} from "@medentic/polaris-react";
import { validateEventRequiredFields } from "@shared/lib/eventValidation";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "@shared/lib";
import { RootState } from "@app/store/store";
import { DeleteConfirmationModal } from "@shared/ui";
import {
  ExternalIcon,
  PersonFilledIcon,
  StopCircleIcon,
  DeleteIcon,
  DuplicateIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { EventBookType, Event, Instructor, Venue, Brand } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";
import OverviewCard from "./components/OverviewCard";
import StatusCard from "@features/events/components/StatusCard";
import { StatusEntityType } from "@features/events/types";
import TranslationsCard, { TranslationsEntityType } from "@features/events/components/TranslationsCard";
import EventTypeAudienceCard from "./components/EventTypeAudienceCard";
import EventPaymentCard from "./components/EventPaymentCard";
import EventDetailsSkeleton from "./components/EventDetailsSkeleton";
import { NoteTone } from "@shared/ui";
import EventNotes from "./components/EventNotes";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { useMyBrand } from "@features/events/hooks/useMyBrand";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import InstructorsCard from "./components/instructors/InstructorsCard";
import usePageMeta from "@shared/hooks/common/usePageMeta";
import WhyAttendCard from "./components/WhyAttendCard";
import LocationsCard from "./components/locations/LocationsCard";
import PartnerBrandsCard from "./components/PartnerBrandsCard";
import OrganizersCard from "./components/organizers/OrganizersCard";
import FAQCard from "./components/faq/FAQCard";
import AttendeeTestimonialsCard from "./components/AttendeeTestimonialsCard";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import EventPhotosVideosCard from "./components/media/EventPhotosVideosCard";
import AgendaCard from "./components/agenda/AgendaCard";
import { URLLinkBuilder } from "@shared/lib";
import RelatedEventsCard from "./components/related/RelatedEventsCard";
import SeatsOverrideModal from "./components/SeatsOverrideModal";
import DuplicateEventModal from "./components/DuplicateEventModal";
import { duplicateEvent } from "@features/events/utils/duplicateEventLogic";

type EventPayload = Omit<Partial<Event>, "timezone"> & { timezone?: string };

const EXISTING_EVENT_INITIAL_DATA: Partial<Event> = {
  organizerIds: [],
};

const createNewEventInitialData = (): Partial<Event> => ({
  title: { bg: "", en: "" },
  description: { bg: "", en: "" },
  slug: "",
  featured: false,
  verified: false,
  visibility: 0,
  languageCodes: [],
  priceCurrency: EVENT_CURRENCY_DEFAULT,
  spotsMax: 100,
  book: EventBookType.PAY,
  sourceUrl: null,
  bookRedirectLink: null,
  tickets: undefined,
  instructorIds: undefined,
  categoryIds: undefined,
  expertiseLevelIds: undefined,
  brandIds: undefined,
  venueIds: undefined,
  organizerIds: undefined,
  gallery: undefined,
  whyAttend: undefined,
  faq: undefined,
  testimonials: undefined,
  agenda: undefined,
  relatedEvents: undefined,
  dateStart: new Date().toISOString().split("T")[0],
  dateEnd: new Date().toISOString().split("T")[0],
  publicURL: undefined,
});

const mapEventToDraftData = (event: Event | undefined): Partial<Event> => {
  if (!event) {
    return EXISTING_EVENT_INITIAL_DATA;
  }

  return {
    id: event.id,
    title: event.title,
    description: event.description,
    slug: event.slug,
    featured: event.featured,
    verified: event.verified,
    visibility: event.visibility,
    formatId: event.formatId,
    expertiseLevelIds: event.expertiseLevelIds,
    categoryIds: event.categoryIds,
    languageCodes: event.languageCodes,
    priceCurrency: event.priceCurrency,
    spotsMax: event.spotsMax,
    book: event.book || "pay",
    sourceUrl: event.sourceUrl,
    bookRedirectLink: event.bookRedirectLink,
    tickets: event.tickets,
    instructorIds: event.instructorIds,
    organizerIds: event.organizerIds,
    brandIds: event.brandIds,
    venueIds: event.venueIds,
    timezone: event.timezone,
    gallery: event.gallery,
    whyAttend: event.whyAttend,
    faq: event.faq,
    testimonials: event.testimonials,
    agenda: event.agenda,
    relatedEvents: event.relatedEvents,
    dateStart: event.dateStart,
    dateEnd: event.dateEnd,
    privateNote: event.privateNote,
    privateNoteTone: event.privateNoteTone,
    enableOnlinePayments: event.enableOnlinePayments,
    publicURL: event.publicURL,
  };
};

const resolveCustomSourceUrl = (
  sourceUrlPattern: string | undefined,
  slug: string | undefined
): string | null => {
  if (!sourceUrlPattern?.trim()) {
    return null;
  }

  return sourceUrlPattern.replace("{slug}", slug ?? "");
};

export default function EventDetails() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, eventId } = useParams<{
    eventOrganizerId: string;
    eventId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { GET, POST, PUT, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const { toneOptions } = useEventMetadata();
  const dispatch = useDispatch();
  const { myBrand } = useMyBrand(hcc);
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );

  // Determine if we're creating a new event
  const isNewEvent = eventId === "new";

  // State
  const [eventDataDraft, setEventDataDraft] = useState<Partial<Event> | null>(
    isNewEvent ? createNewEventInitialData() : null
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(isNewEvent); // New events start as dirty
  const [translationsExpanded, setTranslationsExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSeatsOverride, setShowSeatsOverride] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Fetch event data (only for existing events)
  const {
    data: event,
    isLoading,
    isFetching,
  } = useQuery<Event>({
    queryKey: ["event", hcc, eventId],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events/${eventId}`),
    enabled: Boolean(hcc && eventId && !isNewEvent),
  });

  // Fetch all instructors for AI context (shared with InstructorsCard)
  const { data: allInstructors = [] } = useQuery<Instructor[]>({
    queryKey: ["all-instructors", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/instructors`),
    enabled: Boolean(hcc && !isNewEvent),
  });

  // Fetch all venues for AI context (shared with LocationsCard)
  const { data: allVenues = [] } = useQuery<Venue[]>({
    queryKey: ["venues", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/venues`),
    enabled: Boolean(hcc && !isNewEvent),
  });

  // Filter instructors and venues by event IDs for AI context
  const eventInstructors = event?.instructorIds
    ?.map((id) => allInstructors.find((inst) => inst.id === id))
    .filter(Boolean) as Instructor[] | undefined;
  
  const eventVenues = event?.venueIds
    ?.map((id) => allVenues.find((venue) => venue.id === id))
    .filter(Boolean) as Venue[] | undefined;

  const initialEventData = useMemo(
    () =>
      isNewEvent ? createNewEventInitialData() : mapEventToDraftData(event),
    [isNewEvent, event]
  );
  const eventData = eventDataDraft ?? initialEventData;
  const brandCustomSystem = myBrand?.meta?.customSystem ?? null;
  const customSourceUrlPattern = brandCustomSystem?.sourceUrlPattern?.trim();
  const isCustomSourceMode = Boolean(customSourceUrlPattern);
  const customPreviewUrl = useMemo(
    () => resolveCustomSourceUrl(customSourceUrlPattern, eventData.slug),
    [customSourceUrlPattern, eventData.slug]
  );

  // Prepare page meta data
  const eventTitle = getLocalizedText(event?.title);
  const eventImage = event?.gallery?.media?.find(
    (media) => media.type === "img"
  )?.source;
  const eventImageUrl =
    typeof eventImage === "string"
      ? eventImage
      : Array.isArray(eventImage)
      ? eventImage[0]?.src
      : undefined;

  // Update page meta when event data is available
  usePageMeta(eventTitle, eventImageUrl);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: EventPayload) => {
      if (isNewEvent) {
        return POST(`/api/healthcare-companies/${hcc}/e/events`, data);
      } else {
        return PUT(
          `/api/healthcare-companies/${hcc}/e/events/${eventId}`,
          data
        );
      }
    },
    onSuccess: () => {
      if (isNewEvent) {
        // For new events, navigate back to events list and refresh
        queryClient.invalidateQueries({ queryKey: ["events", hcc] });
        navigate(`/e/${hcc}/events`);
      } else {
        queryClient.invalidateQueries({ queryKey: ["event", hcc, eventId] });
        queryClient.invalidateQueries({ queryKey: ["events", hcc] });
        setEventDataDraft(null);
      }
      setIsDirty(false);
      setFieldErrors({});
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string> } };
        };
        if (apiError.response?.data?.errors) {
          setFieldErrors(apiError.response.data.errors);
        }
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${hcc}/e/events/${eventId}`),
    onSuccess: () => {
      // Invalidate queries and navigate back to events list
      queryClient.invalidateQueries({ queryKey: ["events", hcc] });
      navigate(`/e/${hcc}/events`);
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: (options: {
      copyTickets: boolean;
      copyAgenda: boolean;
      copyGallery: boolean;
    }) => duplicateEvent(event!, hcc!, options, POST, GET),
    onSuccess: (result) => {
      if (result.success && result.newEventId) {
        queryClient.invalidateQueries({ queryKey: ["events", hcc] });
        navigate(`/e/${hcc}/events/${result.newEventId}`);
        dispatch(
          showToast({
            message: t("eventDetails.duplicate.success"),
            type: "success",
          })
        );
        setShowDuplicateModal(false);
      } else {
        dispatch(
          showToast({
            message: result.error || t("eventDetails.duplicate.error"),
            type: "error",
          })
        );
      }
    },
    onError: () => {
      dispatch(
        showToast({
          message: t("eventDetails.duplicate.error"),
          type: "error",
        })
      );
    },
  });

  // Handle data changes
  const handleDataChange = useCallback(
    (updates: Partial<Event>) => {
      const newEventData = { ...eventData, ...updates };

      if (isCustomSourceMode) {
        newEventData.sourceUrl = resolveCustomSourceUrl(
          customSourceUrlPattern,
          newEventData.slug
        );
      }

      setEventDataDraft(newEventData);
      setIsDirty(true);

      // Clear validation errors for fields that are now valid
      setFieldErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        // Check each updated field and clear its error if it's now valid
        Object.keys(updates).forEach((field) => {
          switch (field) {
            case "title":
              if (updates.title) {
                if (selectedLanguages.length > 0) {
                  // Check if all selected languages have content
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.title?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors.title;
                  }
                } else {
                  // Fallback: check if any language has content
                  if (
                    Object.values(updates.title).some((value) => value?.trim())
                  ) {
                    delete newErrors.title;
                  }
                }
              }
              break;
            case "slug":
              if (updates.slug?.trim()) {
                delete newErrors.slug;
              }
              break;
            case "description":
              if (updates.description) {
                if (selectedLanguages.length > 0) {
                  // Check if all selected languages have content
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.description?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors.description;
                  }
                } else {
                  // Fallback: check if any language has content
                  if (
                    Object.values(updates.description).some((value) =>
                      value?.trim()
                    )
                  ) {
                    delete newErrors.description;
                  }
                }
              }
              break;
            case "formatId":
              if (updates.formatId?.trim()) {
                delete newErrors.formatId;
              }
              break;
            case "expertiseLevelIds":
              if (
                updates.expertiseLevelIds &&
                updates.expertiseLevelIds.length > 0
              ) {
                delete newErrors.expertiseLevelIds;
              }
              break;
            case "categoryIds":
              if (updates.categoryIds && updates.categoryIds.length > 0) {
                delete newErrors.categoryIds;
              }
              break;
            case "languageCodes":
              if (updates.languageCodes && updates.languageCodes.length > 0) {
                delete newErrors.languageCodes;
              }
              break;
            case "instructorIds":
              if (updates.instructorIds && updates.instructorIds.length > 0) {
                delete newErrors.instructorIds;
              }
              break;
            case "venueIds":
              if (updates.venueIds && updates.venueIds.length > 0) {
                delete newErrors.venueIds;
              }
              break;
            case "agenda":
              if (updates.agenda && updates.agenda.length > 0) {
                delete newErrors.agenda;
              }
              break;
            case "timezone":
              if (updates.timezone) {
                delete newErrors.timezone;
              }
              break;
            case "whyAttend":
              if (updates.whyAttend?.title) {
                // Clear whyAttend.title errors if all selected languages are filled
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.whyAttend?.title?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors["whyAttend.title"];
                  }
                }
              }
              break;
            case "gallery":
              if (updates.gallery?.title) {
                // Clear gallery.title errors if all selected languages are filled
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.gallery?.title?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors["gallery.title"];
                  }
                }
              }
              break;
            case "testimonials":
              if (updates.testimonials?.title) {
                // Clear testimonials.title errors if all selected languages are filled
                if (selectedLanguages.length > 0) {
                  const hasAllLanguages = selectedLanguages.every((lang) =>
                    updates.testimonials?.title?.[lang]?.trim()
                  );
                  if (hasAllLanguages) {
                    delete newErrors["testimonials.title"];
                  }
                }
              }
              break;
            case "book":
              // Clear book-related errors when book field changes
              if (updates.book) {
                delete newErrors.book;
                delete newErrors.bookRedirectLink;
              }
              break;
            case "bookRedirectLink":
              // Clear bookRedirectLink error when it changes
              // WebsiteField component handles its own validation
              if (updates.bookRedirectLink?.trim()) {
                delete newErrors.bookRedirectLink;
              }
              break;
            case "sourceUrl":
              // Clear stale source URL errors on any user edit.
              // WebsiteField handles live format/HTTPS validation feedback,
              // while validateEventRequiredFields enforces save-time validation.
              if (typeof updates.sourceUrl === "string") {
                delete newErrors.sourceUrl;
              }
              break;
          }
        });

        if (isCustomSourceMode && updates.slug !== undefined) {
          delete newErrors.sourceUrl;
        }

        return newErrors;
      });
    },
    [eventData, selectedLanguages, isCustomSourceMode, customSourceUrlPattern]
  );

  // Handle save
  const handleSave = useCallback(() => {
    // Validate required fields before saving
    const validationResult = validateEventRequiredFields(
      eventData,
      t,
      selectedLanguages
    );

    if (!validationResult.isValid) {
      // Set field errors and prevent save
      setFieldErrors(validationResult.errors);
      console.log(validationResult.errors);

      // Show toast notification about validation errors
      const errorCount = Object.keys(validationResult.errors).length;
      const translationKey =
        errorCount === 1
          ? "common.validation.requiredFieldsError_one"
          : "common.validation.requiredFieldsError_other";

      dispatch(
        showToast({
          message: t(translationKey, {
            count: errorCount,
            defaultValue: `Please fill in all required fields. ${errorCount} field${
              errorCount > 1 ? "s" : ""
            } need${errorCount > 1 ? "" : "s"} attention.`,
          }),
          type: "error",
        })
      );

      return;
    }

    // Check if all selected instructors have public profiles (visibility > 0)
    if (eventData.instructorIds && eventData.instructorIds.length > 0) {
      // Get fresh instructor data from React Query cache (check both cache keys used in the app)
      const allInstructorsCache = queryClient.getQueryData<Instructor[]>(["all-instructors", hcc]) || [];
      const assignInstructorsCache = queryClient.getQueryData<Instructor[]>(["assign-instructors", hcc]) || [];
      
      // Merge both caches and deduplicate by id
      const cachedInstructors = [...allInstructorsCache];
      assignInstructorsCache.forEach((inst) => {
        if (!cachedInstructors.find((existing) => existing.id === inst.id)) {
          cachedInstructors.push(inst);
        }
      });
      
      if (cachedInstructors.length > 0) {
        const nonPublicInstructors = eventData.instructorIds
          .map((id) => cachedInstructors.find((inst) => inst.id === id))
          .filter((inst): inst is Instructor => inst !== undefined && inst.visibility !== 1);

        if (nonPublicInstructors.length > 0) {
          const instructorNames = nonPublicInstructors
            .map((inst) => inst.name || `${inst.firstname?.en || inst.firstname?.bg || ''} ${inst.lastname?.en || inst.lastname?.bg || ''}`.trim())
            .join(", ");

          setFieldErrors((prev) => ({
            ...prev,
            instructorIds: t("eventDetails.instructors.validation.nonPublicInstructors", {
              names: instructorNames,
              defaultValue: `The following instructors must have public profiles before saving: ${instructorNames}`,
            }),
          }));

          dispatch(
            showToast({
              message: t("eventDetails.instructors.validation.nonPublicInstructorsToast", {
                count: nonPublicInstructors.length,
                defaultValue: `${nonPublicInstructors.length} instructor(s) must have public profiles before saving this event.`,
              }),
              type: "error",
            })
          );

          return;
        }
      }
    }

    // Check if all selected brands have public profiles (visibility === 1)
    if (eventData.brandIds && eventData.brandIds.length > 0) {
      // Get fresh brand data from React Query cache
      const cachedBrands = queryClient.getQueryData<Brand[]>(["brands", hcc]) || [];
      
      if (cachedBrands.length > 0) {
        const nonPublicBrands = eventData.brandIds
          .map((id) => cachedBrands.find((brand) => brand.id === id))
          .filter((brand): brand is Brand => brand !== undefined && brand.visibility !== 1);

        if (nonPublicBrands.length > 0) {
          const brandNames = nonPublicBrands
            .map((brand) => brand.name?.en || brand.name?.bg || '')
            .join(", ");

          setFieldErrors((prev) => ({
            ...prev,
            brandIds: t("eventDetails.partnerBrands.validation.nonPublicBrands", {
              names: brandNames,
              defaultValue: `The following brands must have public profiles before saving: ${brandNames}`,
            }),
          }));

          dispatch(
            showToast({
              message: t("eventDetails.partnerBrands.validation.nonPublicBrandsToast", {
                count: nonPublicBrands.length,
                defaultValue: `${nonPublicBrands.length} brand(s) must have public profiles before saving this event.`,
              }),
              type: "error",
            })
          );

          return;
        }
      }
    }

    // Check if all selected organizers have public profiles (visibility === 1)
    if (eventData.organizerIds && eventData.organizerIds.length > 0) {
      // Get fresh brand data from React Query cache (organizers are brands)
      const cachedBrands = queryClient.getQueryData<Brand[]>(["brands", hcc]) || [];
      
      if (cachedBrands.length > 0) {
        const nonPublicOrganizers = eventData.organizerIds
          .map((id) => cachedBrands.find((brand) => brand.id === id))
          .filter((brand): brand is Brand => brand !== undefined && brand.visibility !== 1);

        if (nonPublicOrganizers.length > 0) {
          const organizerNames = nonPublicOrganizers
            .map((brand) => brand.name?.en || brand.name?.bg || '')
            .join(", ");

          setFieldErrors((prev) => ({
            ...prev,
            organizerIds: t("eventDetails.organizers.validation.nonPublicOrganizers", {
              names: organizerNames,
              defaultValue: `The following organizers must have public profiles before saving: ${organizerNames}`,
            }),
          }));

          dispatch(
            showToast({
              message: t("eventDetails.organizers.validation.nonPublicOrganizersToast", {
                count: nonPublicOrganizers.length,
                defaultValue: `${nonPublicOrganizers.length} organizer(s) must have public profiles before saving this event.`,
              }),
              type: "error",
            })
          );

          return;
        }
      }
    }

    // Check if instructors in agenda sessions are also in the event's instructor list
    if (eventData.agenda && eventData.agenda.length > 0) {
      const eventInstructorIds = new Set(eventData.instructorIds || []);
      const sessionInstructorIds = new Set<string>();
      
      // Collect all instructor IDs from sessions
      eventData.agenda.forEach((day) => {
        day.swimlines?.forEach((swimline) => {
          swimline.sessions?.forEach((session) => {
            session.instructorIds?.forEach((id) => {
              if (!eventInstructorIds.has(id)) {
                sessionInstructorIds.add(id);
              }
            });
          });
        });
      });

      if (sessionInstructorIds.size > 0) {
        // Get instructor names from cache
        const allInstructorsCache = queryClient.getQueryData<Instructor[]>(["all-instructors", hcc]) || [];
        const assignInstructorsCache = queryClient.getQueryData<Instructor[]>(["assign-instructors", hcc]) || [];
        const cachedInstructors = [...allInstructorsCache];
        assignInstructorsCache.forEach((inst) => {
          if (!cachedInstructors.find((existing) => existing.id === inst.id)) {
            cachedInstructors.push(inst);
          }
        });

        const orphanedInstructorNames = Array.from(sessionInstructorIds)
          .map((id) => {
            const inst = cachedInstructors.find((i) => i.id === id);
            return inst?.name || inst?.nameWithPrefix || `${inst?.firstname?.en || inst?.firstname?.bg || ''} ${inst?.lastname?.en || inst?.lastname?.bg || ''}`.trim() || id;
          })
          .join(", ");

        setFieldErrors((prev) => ({
          ...prev,
          agenda: t("eventDetails.agenda.validation.orphanedSessionInstructors", {
            names: orphanedInstructorNames,
            defaultValue: `The following instructors are assigned to sessions but not to the event: ${orphanedInstructorNames}`,
          }),
        }));

        dispatch(
          showToast({
            message: t("eventDetails.agenda.validation.orphanedSessionInstructorsToast", {
              names: orphanedInstructorNames,
              defaultValue: `Instructors ${orphanedInstructorNames} are assigned to sessions but not to the event. Please add them to the event or remove them from sessions.`,
            }),
            type: "error",
          })
        );

        // Scroll to the agenda card
        const agendaCard = document.getElementById("agenda-card");
        if (agendaCard) {
          agendaCard.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        return;
      }
    }

    // Clear any existing field errors
    setFieldErrors({});

    const timezoneValue = eventData.timezone?.id;
    const normalizedSourceUrl = isCustomSourceMode
      ? resolveCustomSourceUrl(customSourceUrlPattern, eventData.slug)
      : eventData.sourceUrl?.trim()
      ? eventData.sourceUrl.trim()
      : null;
    const data: EventPayload = {
      ...eventData,
      sourceUrl: normalizedSourceUrl,
      timezone: timezoneValue,
    };
    if (data.agenda && data.agenda.length > 1) {
      data.dateStart = data.agenda[0].date;
      data.dateEnd = data.agenda[data.agenda.length - 1].date;
    }
    
    saveMutation.mutate(data);
  }, [eventData, saveMutation, t, dispatch, selectedLanguages, queryClient, hcc, isCustomSourceMode, customSourceUrlPattern]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (event) {
      setEventDataDraft(null);
      setIsDirty(false);
      setFieldErrors({});
    }
  }, [event]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate(`/e/${hcc}/events${location.search}`);
  }, [navigate, hcc, location.search]);

  const handlePreview = useCallback(() => {
    const previewUrl = isCustomSourceMode
      ? customPreviewUrl
      : eventData.slug
      ? URLLinkBuilder.wwwEvent(eventData.slug)
      : null;

    if (previewUrl) {
      window.open(previewUrl, "_blank");
    }
  }, [eventData.slug, isCustomSourceMode, customPreviewUrl]);

  const handleMedenticPreview = useCallback(() => {
    if (eventData.slug) {
      window.open(URLLinkBuilder.wwwEvent(eventData.slug), "_blank");
    }
  }, [eventData.slug]);

  const handleAttendees = useCallback(() => {
    navigate(`/e/${hcc}/attendees?events=${eventId}`);
  }, [navigate, hcc, eventId]);

  const handleSeatsOverride = useCallback(() => {
    setShowSeatsOverride(true);
  }, []);

  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDuplicate = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  }, [deleteMutation]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  if (isLoading && !isNewEvent) {
    return <EventDetailsSkeleton />;
  }

  return (
    <AppPageWrapper>
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            content: isNewEvent ? t("common.create") : t("common.save"),
            onAction: handleSave,
            loading: saveMutation.isPending,
          }}
          discardAction={
            !isNewEvent
              ? {
                  onAction: handleDiscard,
                }
              : undefined
          }
        />
      )}

      {isFetching && <Loading />}

      <Page
        fullWidth
        backAction={{ content: t("common.back"), onAction: handleBack }}
        title={
          isNewEvent
            ? t("eventDetails.createNewEvent")
            : (
                  getLocalizedText(eventData.title) ||
                ""
              ).slice(0, 40) +
              ((Object.values(eventData.title ?? {})[0]?.length ?? 0) > 40
                ? "..."
                : "")
        }
        subtitle={
          isNewEvent
            ? t("eventDetails.createNewEventSubtitle")
            : t("eventDetails.subtitle", {
                count: event?.spotsCurrent || 0,
                total: event?.spotsMax || 0,
              })
        }
        titleMetadata={
          !isNewEvent &&
          event?.status && (
            <EventBadgeRenderer slug={event.status} entity="event" />
          )
        }
        primaryAction={
          !isNewEvent
            ? {
                content: t("eventDetails.actions.preview"),
                icon: ExternalIcon,
                onAction: handlePreview,
              }
            : undefined
        }
        secondaryActions={
          !isNewEvent
            ? [
                {
                  content: t("eventDetails.actions.attendees"),
                  icon: PersonFilledIcon,
                  onAction: handleAttendees,
                },
                {
                  content: t("eventDetails.actions.duplicate"),
                  icon: DuplicateIcon,
                  onAction: handleDuplicate,
                },
                {
                  content: t("eventDetails.more.overrideSeats"),
                  icon: StopCircleIcon,
                  onAction: handleSeatsOverride,
                },
                {
                  content: t("eventDetails.more.delete"),
                  icon: DeleteIcon,
                  destructive: true,
                  onAction: handleDelete,
                },
                ...(isCustomSourceMode
                  ? [
                      {
                        content: t("eventDetails.actions.medeticPreview"),
                        icon: ExternalIcon,
                        onAction: handleMedenticPreview,
                      },
                    ]
                  : []),
              ]
            : undefined
        }
      >
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              <OverviewCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
                brandCustomSystem={brandCustomSystem}
              />

              <EventTypeAudienceCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <LocationsCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <EventPaymentCard
                eventData={eventData}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <InstructorsCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <div id="agenda-card">
                <AgendaCard
                  eventData={JSON.parse(JSON.stringify(eventData))}
                  onDataChange={handleDataChange}
                  fieldErrors={fieldErrors}
                />
              </div>

              <RelatedEventsCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
              />

              <FAQCard
                eventData={eventData}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <WhyAttendCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
                eventInstructors={eventInstructors}
                eventVenues={eventVenues}
              />

              <PartnerBrandsCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
              />

              <AttendeeTestimonialsCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />

              <EventPhotosVideosCard
                eventData={JSON.parse(JSON.stringify(eventData))}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
                eventInstructors={eventInstructors}
                eventVenues={eventVenues}
              />

              <OrganizersCard
                organizerIds={eventData.organizerIds}
                onDataChange={handleDataChange}
                fieldErrors={fieldErrors}
              />
            </BlockStack>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <Layout>
              <Layout.Section>
                <StatusCard
                  entityType={StatusEntityType.EVENT}
                  featured={eventData.featured ?? false}
                  verified={eventData.verified ?? false}
                  visibility={eventData.visibility ?? 0}
                  onDataChange={handleDataChange}
                />
              </Layout.Section>

              <Layout.Section>
                <TranslationsCard
                  entityType={TranslationsEntityType.EVENT}
                  translationsExpanded={translationsExpanded}
                  setTranslationsExpanded={setTranslationsExpanded}
                />
              </Layout.Section>

              <Layout.Section>
                <Card>
                  <EventNotes
                    notes={eventData?.privateNote || ""}
                    tone={(eventData?.privateNoteTone as NoteTone) || "default"}
                    loading={isLoading}
                    onDataChange={handleDataChange}
                    toneOptions={toneOptions}
                  />
                </Card>
              </Layout.Section>
            </Layout>
          </Layout.Section>
        </Layout>
      </Page>

      {!isNewEvent && event && (
        <SeatsOverrideModal
          open={showSeatsOverride}
          onClose={() => setShowSeatsOverride(false)}
          spotsMax={event.spotsMax}
          spotsCurrent={event.spotsCurrent}
          spotsOverride={
            (event as unknown as { spotsOverride?: number | null })
              .spotsOverride ?? null
          }
          onSaved={() => {
            queryClient.invalidateQueries({
              queryKey: ["event", hcc, eventId],
            });
          }}
        />
      )}

      {!isNewEvent && event && (
        <DuplicateEventModal
          open={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          onConfirm={(options) => duplicateMutation.mutate(options)}
          loading={duplicateMutation.isPending}
        />
      )}

      {/* <TranslateModal
        isOpen={translationsExpanded}
        onClose={() => setTranslationsExpanded(false)}
        eventData={eventData}
        onDataChange={handleDataChange}
      /> */}
    </AppPageWrapper>
  );
}
